// Admin detail — operator profile + roles + permissions. Built on the
// same scaffold as the user detail page but lighter: there's no
// wallet, no positions/orders, no per-user audit timeline (the global
// audit log lives at /audit). Header carries lock / unlock + display-
// name edit so the list table can stay clickable-only.
//
// Column order is deliberate (SM-346): the left rail runs grants →
// commission → authentication, with the full permission expansion LAST
// and collapsed — dozens of codes were pushing commission and activity
// below a long scroll, and exact codes are reference material, not
// something an operator reads on every visit. Recent activity tops the
// right rail; the old Profile card duplicated the header field-for-
// field and is gone (its one unique fact, the created date, lives in
// the header meta line now).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { fetchAdmin, fetchRoles, setAdminStatus, assignAdminRole, revokeAdminRole, keys } from "@/lib/queries";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Select } from "@/components/ui/Select";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { FormModal } from "@/components/ui/FormModal";
import { Avatar } from "@/components/ui/Avatar";
import { AvatarUploader } from "@/components/admins/AvatarUploader";
import { CommissionDefaultCard } from "@/components/admins/CommissionDefaultCard";
import { ResetPasswordAction } from "@/components/admins/ResetPasswordAction";
import { fmtDate, fmtRelative, statusLabel } from "@/lib/format";
import type { AdminAuditEvent, AdminMFAFactorItem, AdminSessionItem } from "@/lib/types";

const ROLES_PAGE = { limit: 200, offset: 0 } as const;

export const Route = createFileRoute("/_app/admins/$id")({
  component: AdminDetailPage,
});

type Pending = "lock" | "unlock" | null;

function AdminDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: keys.adminDetail(id),
    queryFn: () => fetchAdmin(id),
  });
  const roles = useQuery({
    queryKey: keys.rolesList(ROLES_PAGE.limit, ROLES_PAGE.offset),
    queryFn: () => fetchRoles(ROLES_PAGE),
  });

  const myPerms = useMyPerms();

  const [pending, setPending] = useState<Pending>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const status = useMutation({
    mutationFn: (st: string) => setAdminStatus(id, st),
    onSuccess: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: keys.admins });
      qc.invalidateQueries({ queryKey: keys.adminDetail(id) });
    },
    meta: { successMessage: "Admin status updated." },
  });
  const closeModal = () => {
    status.reset();
    setPending(null);
  };

  const [roleToAdd, setRoleToAdd] = useState("");
  const assign = useMutation({
    mutationFn: (r: string) => assignAdminRole(id, r),
    onSuccess: () => {
      setRoleToAdd("");
      qc.invalidateQueries({ queryKey: keys.adminDetail(id) });
    },
    meta: { successMessage: "Role assigned." },
  });
  const revoke = useMutation({
    mutationFn: (r: string) => revokeAdminRole(id, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adminDetail(id) }),
    meta: { successMessage: "Role revoked." },
  });

  const admin = detail.data?.admin;
  const adminRoles = detail.data?.roles ?? [];
  const adminPerms = detail.data?.permissions ?? [];
  const factors = detail.data?.mfa_factors ?? [];
  const sessions = detail.data?.sessions ?? [];
  const audit = detail.data?.audit ?? [];
  const activeFactors = factors.filter((f) => f.status === "ACTIVE");
  const activeSessions = sessions.filter((s) => s.status === "ACTIVE");
  const isActive = admin?.status === "ACTIVE";
  const grantableRoles = (roles.data?.items ?? [])
    .map((r) => r.name)
    .filter((n) => !adminRoles.includes(n));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admins" className="text-2xs text-muted hover:text-fg">← back to admins</Link>
      </div>

      {detail.isLoading && <Skeleton className="h-40" />}
      {detail.data && admin && (
        <>
          <ProfileHeader
            id={id}
            email={admin.email}
            displayName={admin.display_name}
            avatarUrl={admin.avatar_url}
            statusName={admin.status}
            isActive={isActive}
            createdAt={admin.created_at}
            mustChangePassword={admin.must_change_password}
            passwordChangedAt={admin.password_changed_at}
            canResetPassword={myPerms.has(Perm.AdminUpdate)}
            isSelf={myPerms.adminId === id}
            onLock={() => setPending("lock")}
            onUnlock={() => setPending("unlock")}
            onEditPhoto={() => setAvatarOpen(true)}
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi label="Roles assigned" value={`${adminRoles.length}`} />
            <Kpi label="Permissions effective" value={`${adminPerms.length}`} />
            <Kpi
              label="MFA factors"
              value={`${activeFactors.length}`}
              sub={`${factors.length} enrolled total`}
            />
            <Kpi
              label="Active sessions"
              value={`${activeSessions.length}`}
              sub={`${sessions.length} on record`}
            />
            <Kpi
              label="Last login"
              value={admin.last_login_at ? fmtRelative(admin.last_login_at) : "never"}
              sub={admin.last_login_at ? fmtDate(admin.last_login_at) : undefined}
            />
          </div>

          {/* min-w-0 on both columns: below lg the grid has no explicit
              template, so the auto track floors at the widest card's
              min-content and the whole page scrolls sideways on phones. */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <Card>
                <CardHeader className="border-b border-border">
                  <CardTitle>Roles</CardTitle>
                  <span className="text-2xs text-muted">{adminRoles.length}</span>
                </CardHeader>
                <CardBody className="space-y-4 pt-5">
                  {adminRoles.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-line-1 bg-bg-2 px-4 py-5 text-center text-2xs text-muted">
                      No roles assigned — this operator has no effective permissions.
                    </p>
                  ) : (
                    <ul className="flex flex-wrap gap-2">
                      {adminRoles.map((r) => (
                        <li
                          key={r}
                          className="group flex items-center gap-2 rounded-full border border-line-2 bg-neon/8 px-3 py-1 text-2xs font-medium text-fg"
                        >
                          <span>{r}</span>
                          <button
                            type="button"
                            onClick={() => revoke.mutate(r)}
                            className="text-muted transition-colors hover:text-no"
                            aria-label={`Revoke ${r}`}
                            disabled={revoke.isPending}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {grantableRoles.length > 0 && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="w-56">
                        <Select
                          placeholder="Add role…"
                          value={roleToAdd}
                          onChange={(e) => setRoleToAdd(e.target.value)}
                          options={grantableRoles.map((n) => ({ value: n, label: n }))}
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => roleToAdd && assign.mutate(roleToAdd)}
                        loading={assign.isPending}
                        disabled={!roleToAdd}
                      >
                        Grant
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Commission directly under the grant UI: it is the third
                  thing being an operator confers, and the only one that
                  pays out. */}
              <CommissionDefaultCard
                adminId={id}
                adminLabel={admin.display_name || admin.email}
              />

              <SecurityCard factors={factors} sessions={sessions} />

              <PermissionsCard codes={adminPerms} />
            </div>

            <div className="min-w-0 space-y-6">
              <AuditCard events={audit} />
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        open={pending === "lock"}
        tone="danger"
        title="Lock this operator account?"
        description={
          <p>
            <span className="font-mono text-2xs text-t-2">{admin?.email}</span> will be unable to sign in to the
            backoffice. Their existing audit history is preserved.
          </p>
        }
        confirmLabel="Lock operator"
        cancelLabel="Keep active"
        loading={status.isPending}
        error={status.isError ? (status.error as Error).message : null}
        onConfirm={() => status.mutate("LOCKED")}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "unlock"}
        tone="primary"
        title="Unlock this operator account?"
        description={
          <p>
            Restores sign-in access for <span className="font-mono text-2xs text-t-2">{admin?.email}</span>.
          </p>
        }
        confirmLabel="Unlock operator"
        cancelLabel="Keep locked"
        loading={status.isPending}
        error={status.isError ? (status.error as Error).message : null}
        onConfirm={() => status.mutate("ACTIVE")}
        onCancel={closeModal}
      />

      {admin && (
        <FormModal
          open={avatarOpen}
          title={`Profile picture — ${admin.display_name || admin.email}`}
          confirmLabel="Done"
          canSubmit
          onSubmit={() => setAvatarOpen(false)}
          onCancel={() => setAvatarOpen(false)}
        >
          <AvatarUploader
            adminId={id}
            currentUrl={admin.avatar_url}
            name={admin.display_name || admin.email}
          />
        </FormModal>
      )}
    </div>
  );
}

function ProfileHeader({
  id,
  email,
  displayName,
  avatarUrl,
  statusName,
  isActive,
  createdAt,
  mustChangePassword,
  passwordChangedAt,
  canResetPassword,
  isSelf,
  onLock,
  onUnlock,
  onEditPhoto,
}: {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  statusName: string;
  isActive: boolean;
  createdAt?: string;
  mustChangePassword?: boolean;
  passwordChangedAt?: string;
  canResetPassword: boolean;
  isSelf: boolean;
  onLock: () => void;
  onUnlock: () => void;
  onEditPhoto: () => void;
}) {
  const display = displayName || email;
  return (
    <div className="card relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 220px at 0% 0%, rgba(184, 91, 255, 0.10), transparent 60%)",
        }}
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Avatar url={avatarUrl} name={display} size={56} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl tracking-tight text-fg">{display}</h1>
              <Badge tone={isActive ? "success" : statusName === "LOCKED" ? "warning" : "muted"}>
                {statusLabel(statusName)}
              </Badge>
              <Badge tone="accent">Operator</Badge>
              {/* An account still on an operator-issued password is
                  worth seeing at a glance: it is the one state where a
                  second person knows the credential. */}
              {mustChangePassword && (
                <Badge tone="warning">Temporary password</Badge>
              )}
            </div>
            <div className="mt-1 text-xs text-muted">
              {email}
              {createdAt && <span> · joined {fmtDate(createdAt)}</span>}
              <span>
                {" · "}
                {passwordChangedAt
                  ? `password set ${fmtRelative(passwordChangedAt)}`
                  : "password never changed"}
              </span>
            </div>
            <div className="mt-0.5 font-mono text-2xs text-muted">{id}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onEditPhoto}>Edit photo</Button>
          {/* ADMIN_UPDATE only — the same permission the server gates
              the endpoint on. Hiding it is courtesy; the refusal is
              backoffice-side and holds if the UI is bypassed. */}
          {canResetPassword && (
            <ResetPasswordAction adminId={id} adminEmail={email} isSelf={isSelf} />
          )}
          {isActive ? (
            <Button variant="danger" onClick={onLock}>Lock operator</Button>
          ) : (
            <Button variant="primary" onClick={onUnlock}>Unlock operator</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <div className="text-2xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="num mt-1.5 text-xl font-bold tracking-tight text-fg">{value}</div>
      {sub && <div className="mt-1 text-2xs text-muted">{sub}</div>}
    </div>
  );
}

// SecurityCard surfaces MFA factors + recent sessions side-by-side
// so an operator reviewing another admin can spot a stale enrollment
// or an unfamiliar device without leaving the page. Mirrors the user
// detail page's AuthenticationCard but trimmed: admins don't have
// linked external identities.
function SecurityCard({
  factors,
  sessions,
}: {
  factors: AdminMFAFactorItem[];
  sessions: AdminSessionItem[];
}) {
  const active = factors.filter((f) => f.status === "ACTIVE").length;
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Authentication</CardTitle>
        <span className="text-2xs text-muted">
          {active} {active === 1 ? "factor" : "factors"} · {sessions.length} {sessions.length === 1 ? "session" : "sessions"}
        </span>
      </CardHeader>
      <CardBody className="space-y-5 pt-5">
        <Section title="MFA factors">
          {factors.length === 0 ? (
            <EmptyHint>No MFA factors enrolled — operator relies on password only.</EmptyHint>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {factors.map((f) => (
                <li
                  key={f.factor_id}
                  className="flex items-center gap-3 rounded-lg border border-line-1 bg-bg-2 px-3 py-2.5"
                >
                  <FactorIcon kind={f.kind} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs font-medium text-fg">{f.friendly_name || f.kind}</span>
                      <Badge
                        tone={
                          f.status === "ACTIVE"
                            ? "success"
                            : f.status === "PENDING_VERIFICATION"
                              ? "warning"
                              : "muted"
                        }
                      >
                        {statusLabel(f.status)}
                      </Badge>
                    </div>
                    <div className="mt-0.5 text-2xs text-muted">
                      {f.kind}
                      {f.last_used_at ? ` · used ${fmtRelative(f.last_used_at)}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recent sessions">
          {sessions.length === 0 ? (
            <EmptyHint>No sessions on record.</EmptyHint>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-line-1">
              {sessions.slice(0, 5).map((s) => (
                <li key={s.session_id} className="flex items-center gap-3 px-3 py-2.5">
                  <DeviceIcon ua={s.user_agent} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-xs font-medium text-fg">{s.ip_address || "Unknown IP"}</span>
                      <Badge tone={s.status === "ACTIVE" ? "success" : "muted"}>{statusLabel(s.status)}</Badge>
                    </div>
                    <div className="mt-0.5 truncate text-2xs text-muted">
                      {s.user_agent || "—"}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-2xs text-muted">
                    <div>seen {fmtRelative(s.last_seen_at || s.issued_at)}</div>
                    {s.expires_at && <div>exp {fmtRelative(s.expires_at)}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </CardBody>
    </Card>
  );
}

function FactorIcon({ kind }: { kind: string }) {
  const map: Record<string, string> = {
    TOTP: "🔐",
    EMAIL_OTP: "✉︎",
    WEBAUTHN: "🔑",
    PASSKEY: "🔑",
    PASSPHRASE: "⌨︎",
    BACKUP_CODE: "🛟",
  };
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-line-1 bg-bg-3 text-sm"
    >
      {map[kind] ?? "🔒"}
    </span>
  );
}

function DeviceIcon({ ua }: { ua?: string }) {
  const isMobile = /Android|iPhone|iPad/i.test(ua ?? "");
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-line-1 bg-bg-3 text-base"
    >
      {isMobile ? "📱" : "💻"}
    </span>
  );
}

// AuditCard mirrors the user-detail timeline shape but pulls from the
// backoffice audit log keyed by admin_id (every action this admin
// performed). Severity drives the dot colour so the eye picks up
// CRITICAL / ERROR rows first.
function AuditCard({ events }: { events: AdminAuditEvent[] }) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Recent activity</CardTitle>
        <span className="text-2xs text-muted">{events.length}</span>
      </CardHeader>
      <CardBody className="pt-5">
        {events.length === 0 ? (
          <EmptyHint>No recent admin actions.</EmptyHint>
        ) : (
          <ol className="relative ml-2 space-y-3 border-l border-line-1 pl-5">
            {events.slice(0, 12).map((e) => (
              <li key={e.event_id} className="relative">
                <span
                  aria-hidden="true"
                  className={`absolute -left-[27px] top-1 grid h-3 w-3 place-items-center rounded-full border-2 border-bg-1 ${severityDot(e.severity)}`}
                />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-fg">{e.event_type}</span>
                      <Badge tone={severityTone(e.severity)}>{e.severity}</Badge>
                    </div>
                    {e.summary && <div className="mt-0.5 text-2xs text-muted">{e.summary}</div>}
                    {(e.target_kind || e.ip_address) && (
                      <div className="mt-0.5 text-2xs text-muted">
                        {[e.target_kind && `${e.target_kind}:${e.target_id ?? ""}`, e.ip_address]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 text-2xs text-muted">{fmtRelative(e.occurred_at)}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function severityTone(s: string): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "CRITICAL":
    case "ERROR":
      return "danger";
    case "WARN":
      return "warning";
    case "INFO":
      return "success";
    default:
      return "muted";
  }
}

function severityDot(s: string): string {
  switch (s) {
    case "CRITICAL":
    case "ERROR":
      return "bg-no";
    case "WARN":
      return "bg-warn";
    case "INFO":
      return "bg-yes";
    default:
      return "bg-bg-4";
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-muted">{title}</div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line-1 bg-bg-2 px-4 py-5 text-center text-2xs text-muted">
      {children}
    </div>
  );
}

// Permission codes are SCREAMING_SNAKE ("CRYPTO_WALLET_CONFIG"), so the
// namespace is the first underscore token — the previous grouping split
// on "." and put every code in a group of its own, which is exactly the
// wall of headers that buried the rest of this page. Single-word grants
// (READONLY, FINANCE, SUPPORT) are umbrella roles, pooled under GENERAL.
function permissionGroups(codes: string[]): [string, string[]][] {
  const groups: Record<string, string[]> = {};
  for (const c of codes) {
    const us = c.indexOf("_");
    const key = us === -1 ? "GENERAL" : c.slice(0, us);
    (groups[key] ??= []).push(c);
  }
  return Object.keys(groups)
    .sort()
    .map((g) => [g, groups[g]!.sort()]);
}

// PermissionsCard — the exact codes are reference material an operator
// needs while debugging a 403, not on every visit. Collapsed (the
// default) it is one namespace-summary row; any pill or the header
// toggle expands the full grouped grid.
function PermissionsCard({ codes }: { codes: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const groups = permissionGroups(codes);
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Effective permissions</CardTitle>
        {codes.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="text-2xs font-medium text-muted transition-colors hover:text-fg"
          >
            {expanded ? "Collapse" : `Show all ${codes.length} codes`}
          </button>
        )}
      </CardHeader>
      <CardBody className="pt-5">
        {codes.length === 0 ? (
          <EmptyHint>No effective permissions.</EmptyHint>
        ) : expanded ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {groups.map(([g, list]) => (
              <div key={g}>
                <div className="text-2xs font-semibold uppercase tracking-wide text-muted">
                  {g} <span className="num">· {list.length}</span>
                </div>
                <ul className="mt-2 flex flex-wrap gap-1">
                  {list.map((c) => (
                    <li
                      key={c}
                      className="rounded-md border border-line-1 bg-bg-2 px-2 py-0.5 font-mono text-2xs text-fg"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {groups.map(([g, list]) => (
              <li key={g}>
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  aria-label={`Show ${g} permissions (${list.length})`}
                  className="flex items-center gap-1.5 rounded-full border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-2 transition-colors hover:border-line-2 hover:text-fg"
                >
                  {g}
                  <span className="num text-muted">{list.length}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
